// @ts-check
/**
 * Generátor drilov.
 *
 * Kľúčová vlastnosť: generátor NIKDY nevygeneruje znak mimo `allowedKeys`.
 * Vďaka tomu sa žiak v lekcii nestretne s klávesou, ktorú sa ešte neučil —
 * a to je presne to, čo robí výučbu výučbou a nie náhodným textom.
 *
 * Generovanie je deterministické (seed), takže tá istá lekcia vyzerá vždy rovnako:
 * dá sa testovať, opravovať a je porovnateľná medzi používateľmi.
 */

/**
 * Deterministický PRNG (mulberry32).
 * @param {number} seed
 */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * @param {() => number} rnd
 * @param {string} chars
 */
function pick(rnd, chars) {
  return chars[Math.floor(rnd() * chars.length)];
}

/**
 * @param {string[]} items
 * @param {() => number} rnd
 */
function shuffle(items, rnd) {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * @typedef {Object} DrillOptions
 * @property {string[]} allowedKeys   znaky, ktoré sa smú v lekcii objaviť
 * @property {string[]} [newKeys]     novo naučené znaky, ktoré majú dostať priestor
 * @property {number} [seed]
 * @property {number} [lines]         koľko riadkov vygenerovať
 * @property {number} [groupsPerLine]
 * @property {'pairs'|'syllables'|'random'|'words'} [pattern]
 * @property {string[]} [words]       slovník (len pre pattern 'words')
 */

/**
 * @param {DrillOptions} options
 * @returns {string[]} riadky cvičenia
 */
export function generateDrill(options) {
  const {
    allowedKeys, newKeys = [], seed = 1, lines = 4, groupsPerLine = 6,
    pattern = 'random', words = [],
  } = options;

  if (allowedKeys.length === 0) return [];
  const rnd = mulberry32(seed);
  const pool = allowedKeys.join('');
  const result = [];

  // Nové znaky sa v skupinách striedajú v pevnom poradí (nie náhodne). Vďaka tomu
  // sa každý nový znak v cvičení NAOZAJ objaví, ak je skupín aspoň toľko ako znakov.
  // Pri náhodnom výbere by sa mohlo stať, že sa niektorý znak nevyskytne vôbec.
  let newKeyIndex = 0;

  for (let line = 0; line < lines; line++) {
    const groups = [];

    if (pattern === 'words') {
      const usable = words.filter((w) => [...w].every((ch) => allowedKeys.includes(ch)));
      if (usable.length > 0) {
        const chosen = shuffle(usable, rnd);
        for (let i = 0; i < groupsPerLine; i++) {
          groups.push(chosen[i % chosen.length]);
        }
        result.push(groups.join(' '));
        continue;
      }
      // Žiadne slovo sa nedá poskladať z naučených znakov — radšej skupiny znakov
      // než dlhý reťazec celého znakového fondu (to by nebolo čitateľné cvičenie).
      for (let g = 0; g < groupsPerLine; g++) {
        const chars = [];
        while (chars.length < 3) chars.push(pick(rnd, pool));
        groups.push(chars.join(''));
      }
      result.push(groups.join(' '));
      continue;
    }

    for (let g = 0; g < groupsPerLine; g++) {
      const length = pattern === 'pairs' ? 2 : 3;
      const chars = [];

      if (newKeys.length > 0) {
        // Každá skupina obsahuje aspoň jeden novo naučený znak — a to v stálom poradí.
        chars.push(newKeys[newKeyIndex++ % newKeys.length]);
      }
      while (chars.length < length) {
        chars.push(pick(rnd, pool));
      }

      if (pattern === 'pairs') {
        // Dvojica prstov: nový znak + znak z rovnakej alebo susednej ruky.
        groups.push(chars[0] + chars[1]);
      } else {
        const ordered = shuffle(chars, rnd);
        groups.push(ordered.join(''));
      }
    }

    result.push(groups.join(' '));
  }

  // Kontrola invariantu: nikdy žiadny znak mimo allowedKeys.
  for (const line of result) {
    for (const ch of line) {
      if (ch === ' ') continue;
      if (!allowedKeys.includes(ch)) {
        throw new Error(`Generátor vytvoril znak "${ch}" mimo allowedKeys`);
      }
    }
  }

  return result;
}

/**
 * Vygeneruje krátke vety z naučených slov (režim „Vety“).
 *
 * Vety sa skladajú z templátov, aby sa do cvičenia nikdy nedostal znak mimo
 * `allowedKeys`. Veľké písmeno na začiatku sa použije len vtedy, keď už žiak
 * pozná Shift (teda keď sú veľké písmená v `allowedKeys`).
 *
 * @param {{allowedKeys: string[], words: string[], seed?: number, lines?: number}} options
 * @returns {string[]}
 */
export function generateSentences(options) {
  const { allowedKeys, words, seed = 1, lines = 5 } = options;
  const rnd = mulberry32(seed);
  const allowed = new Set(allowedKeys);
  const usable = words.filter((word) => [...word].every((char) => allowed.has(char)));
  if (usable.length === 0) return [];

  // Veľké písmeno na začiatku vety použijeme len vtedy, keď žiak pozná Shift
  // AJ konkrétne veľké písmeno (v lekcii 23 sa preberá len časť abecedy).
  /** @param {string} word */
  const withCapital = (word) => {
    const capital = word.charAt(0).toUpperCase() + word.slice(1);
    return allowed.has(capital.charAt(0)) ? capital : word;
  };

  // Interpunkcia sa použije len vtedy, keď ju žiak už pozná (bodka sa preberá
  // až v lekcii 24), inak by cvičenie obsahovalo nenaučený znak.
  const end = allowed.has('.') ? '.' : '';
  const comma = allowed.has(',') ? ', ' : ' ';

  const pickWord = () => usable[Math.floor(rnd() * usable.length)];

  /** @type {Array<(w: string[]) => string>} */
  const templates = [
    (w) => `${withCapital(w[0])} ${w[1]}${end}`,
    (w) => `${withCapital(w[0])} ${w[1]} ${w[2]}${end}`,
    (w) => `${withCapital(w[0])} a ${w[1]}${end}`,
    (w) => `${withCapital(w[0])} je ${w[1]}${end}`,
    (w) => `${withCapital(w[0])} ${w[1]}${comma}${w[2]}${end}`,
    (w) => `${withCapital(w[0])} ${w[1]} ${w[2]} ${w[3]}${end}`,
  ];

  // Veta je platná už z konštrukcie: všetky slová sú z `usable` a pridávame len
  // medzery, bodku, čiarku a prípadné veľké písmeno (to len ak je v allowedKeys).
  const result = [];
  for (let line = 0; line < lines; line++) {
    /** @type {string[]} */
    const chosen = [];
    for (let i = 0; i < 4; i++) {
      let word = pickWord();
      let guard = 0;
      while (chosen.includes(word) && usable.length > 4 && guard < 10) {
        word = pickWord();
        guard += 1;
      }
      chosen.push(word);
    }
    const template = templates[Math.floor(rnd() * templates.length)];
    result.push(template(chosen));
  }

  return result;
}

/**
 * Otestuje, či riadky rešpektujú allowedKeys (používa sa pri validácii obsahu).
 * @param {string[]} items
 * @param {string[]} allowedKeys
 * @returns {string[]} zoznam problémov
 */
export function validateItems(items, allowedKeys) {
  /** @type {string[]} */
  const problems = [];
  const allowed = new Set(allowedKeys);
  items.forEach((line, i) => {
    for (const ch of line) {
      if (ch === ' ') continue;
      if (!allowed.has(ch)) {
        problems.push(`riadok ${i + 1}: znak "${ch}" nie je v allowedKeys`);
      }
    }
  });
  return problems;
}
